/**
 * StaffDetailPage — S5: Detalhe do Profissional / Responsabilidades
 * (rota canônica /manager/staff/:staffId).
 *
 * Tabs: Dados | Responsabilidades | Permissões | Atletas.
 * - Dados (P2.3): visualização + edição via ProfessionalForm canônico
 *   (PUT /roster/staff/:id) e "Desativar profissional"
 *   (DELETE /roster/staff/:id — soft delete; nunca chamar de "Excluir").
 *   Erros de mutação são LOCAIS à ação — não substituem a ficha carregada.
 * - Responsabilidades: regras (AND dentro, OR entre, individual pura),
 *   PREVIEW OBRIGATÓRIO antes de salvar (POST /gestor/staff/rules/preview —
 *   mesmo fragmento SQL da view canônica ⇒ preview == persistência).
 * - Editar dimensões NÃO existe em V1: desativa a regra e cria outra
 *   (PATCH is_active soft-disable/reactivate).
 * - Permissões: read-only (staff_tool_access) — reflexo do RBAC, não editor.
 * - Atletas: resolução canônica (vw_staff_accessible_athletes via backend)
 *   com explicabilidade access_sources ("Acesso por: …"). Explicabilidade
 *   explica; NUNCA decide acesso.
 *
 * Visual preto/branco/cinza (padrão G2-UX.1/S4): cor só para status.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AZURE } from '../../services/api';
import ProfessionalForm from '../../components/staff/ProfessionalForm';

const INK = '#111827';
const MUTED = '#6b7280';
const BORDER = '#e5e7eb';
const BG = '#f8fafc';

const ROLE_LABELS = {
  TREINADOR: 'Treinador',
  PREPARADOR_FISICO: 'Prep. Físico',
  NUTRICIONISTA: 'Nutricionista',
  FISIOTERAPEUTA: 'Fisioterapeuta',
  MEDICO: 'Médico',
  ANALISTA: 'Analista',
  AUXILIAR: 'Auxiliar',
  GESTOR: 'Gestor',
};

const SOURCE_LABELS = {
  responsibility_rule: (s) => `Regra: ${s.label}`,
  coach_link: () => 'Vínculo de treinador',
  staff_grant: () => 'Acesso direto',
  gestor_role: () => 'Papel de gestor',
};

const TOOL_LABELS = {
  training: 'Treinos', physical: 'Prep. física', nutrition: 'Nutrição',
  wellness: 'Wellness', competitions: 'Competições', reports: 'Relatórios',
  roster: 'Elenco', calendar: 'Calendário',
};

const TABS = [
  // P2.3: key 'overview' preservada (deep links/estado); só o label mudou.
  { key: 'overview', label: 'Dados' },
  { key: 'rules', label: 'Responsabilidades' },
  { key: 'permissions', label: 'Permissões' },
  { key: 'athletes', label: 'Atletas' },
];

const inputCls = 'px-3 py-2 border rounded-lg text-sm bg-white';
const labelCls = 'block text-[10px] font-bold uppercase mb-1';

/** Hook: carrega uma dimensão do editor com estados distintos —
 *  loading | error (≠ lista vazia) | ok ([] = ausência real). */
const useDimension = (path) => {
  const [items, setItems] = useState(null); // null = loading
  const [error, setError] = useState(false);
  const load = useCallback(() => {
    setItems(null); setError(false);
    AZURE.get(path)
      .then(({ data }) => setItems(data?.data || []))
      .catch(() => { setError(true); setItems([]); });
  }, [path]);
  useEffect(() => { load(); }, [load]);
  return { items: items || [], loading: items === null, error, reload: load };
};

/** Erro de dimensão: NUNCA vira select vazio silencioso (erro ≠ ausência). */
const DimensionError = ({ label, onRetry }) => (
  <div className="flex items-center gap-2 border rounded-lg px-3 py-2"
    style={{ borderColor: '#fca5a5' }}>
    <span className="text-xs" style={{ color: '#b91c1c' }}>
      Não foi possível carregar {label}.
    </span>
    <button onClick={onRetry} className="text-xs font-bold underline"
      style={{ color: INK }}>
      Tentar novamente
    </button>
  </div>
);

/** Editor de nova regra — semântica completa da 381: Categoria AND Sexo AND
 *  Grupo podem coexistir na MESMA regra; Atleta específico é mutuamente
 *  exclusivo (regra individual pura). Preview obrigatório antes de salvar. */
const RuleEditor = ({ staffId, onCreated, onCancel }) => {
  const categoriesDim = useDimension('/roster/categories');
  const squadsDim = useDimension('/gestor/calendar/squads');
  const athletesDim = useDimension('/gestor/calendar/athletes');
  const [categoryId, setCategoryId] = useState('');
  const [gender, setGender] = useState('');
  const [squadId, setSquadId] = useState('');
  const [athleteId, setAthleteId] = useState('');
  const [preview, setPreview] = useState(null); // {count, athletes}
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const individual = athleteId !== '';

  const payload = () => {
    if (individual) return { athlete_user_id: Number(athleteId) };
    return {
      category_id: categoryId ? Number(categoryId) : null,
      gender: gender || null,
      squad_id: squadId ? Number(squadId) : null,
    };
  };
  const hasDimension = () => individual || !!(categoryId || gender || squadId);
  // Salvar bloqueado se uma dimensão NECESSÁRIA (em uso) falhou ao carregar
  const dimensionBlocked = individual
    ? athletesDim.error
    : ((!!categoryId && categoriesDim.error) || (!!squadId && squadsDim.error));

  // Qualquer mudança de dimensão invalida o preview (preview == persistência)
  const resetPreview = () => { setPreview(null); setError(null); };

  const runPreview = async () => {
    setPreviewing(true); setError(null);
    try {
      const { data } = await AZURE.post('/gestor/staff/rules/preview', payload());
      setPreview(data?.data || { count: 0, athletes: [] });
    } catch (e) {
      setError(e?.response?.data?.error || 'Erro ao gerar preview');
    } finally { setPreviewing(false); }
  };

  const save = async () => {
    setSaving(true); setError(null);
    try {
      const { data } = await AZURE.post(`/gestor/staff/${staffId}/rules`, payload());
      onCreated(data?.data);
    } catch (e) {
      setError(e?.response?.data?.error || 'Erro ao salvar regra');
    } finally { setSaving(false); }
  };

  return (
    <div className="border rounded-xl p-4 mb-4" style={{ borderColor: INK }}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-black" style={{ color: INK }}>Nova regra de responsabilidade</h3>
        <button onClick={onCancel} className="text-xs font-semibold" style={{ color: MUTED }}>Cancelar</button>
      </div>

      {/* QUEM ESTE PROFISSIONAL ACOMPANHA?
          Categoria AND Sexo AND Grupo (mesma regra, semântica 381).
          Atleta específico é mutuamente exclusivo (individual pura). */}
      <p className="text-[10px] font-black uppercase tracking-widest mb-2"
        style={{ color: MUTED }}>
        Quem este profissional acompanha?
      </p>
      <div className="flex flex-wrap gap-3 mb-3">
        <div>
          <label className={labelCls} style={{ color: MUTED }}>Categoria</label>
          {categoriesDim.error ? (
            <DimensionError label="as categorias" onRetry={categoriesDim.reload} />
          ) : (
            <select className={inputCls} style={{ borderColor: BORDER, color: INK }}
              disabled={individual || categoriesDim.loading}
              value={categoryId}
              onChange={(e) => { setCategoryId(e.target.value); resetPreview(); }}>
              <option value="">
                {categoriesDim.loading ? 'Carregando…'
                  : categoriesDim.items.length === 0 ? 'Nenhuma categoria disponível'
                  : 'Todas'}
              </option>
              {categoriesDim.items.map((c) => (
                <option key={c.category_id} value={c.category_id}>
                  {c.category_name || c.category_code}
                </option>
              ))}
            </select>
          )}
        </div>
        <div>
          <label className={labelCls} style={{ color: MUTED }}>Sexo</label>
          <select className={inputCls} style={{ borderColor: BORDER, color: INK }}
            disabled={individual}
            value={gender} onChange={(e) => { setGender(e.target.value); resetPreview(); }}>
            <option value="">Todos</option>
            <option value="female">Feminino</option>
            <option value="male">Masculino</option>
          </select>
        </div>
        <div>
          <label className={labelCls} style={{ color: MUTED }}>Grupo</label>
          {squadsDim.error ? (
            <DimensionError label="os grupos" onRetry={squadsDim.reload} />
          ) : (
            <select className={inputCls} style={{ borderColor: BORDER, color: INK }}
              disabled={individual || squadsDim.loading}
              value={squadId}
              onChange={(e) => { setSquadId(e.target.value); resetPreview(); }}>
              <option value="">
                {squadsDim.loading ? 'Carregando…'
                  : squadsDim.items.length === 0 ? 'Nenhum grupo disponível'
                  : 'Todos'}
              </option>
              {squadsDim.items.map((s) => (
                <option key={s.squad_id} value={s.squad_id}>{s.name}</option>
              ))}
            </select>
          )}
        </div>
        <p className="text-[11px] self-end pb-2 w-full" style={{ color: MUTED }}>
          Categoria, sexo e grupo combinam entre si (E). Preencha pelo menos um.
        </p>
      </div>

      {/* ────────── ou ────────── */}
      <div className="flex items-center gap-3 my-3">
        <div className="flex-1 h-px" style={{ backgroundColor: BORDER }} />
        <span className="text-[10px] font-bold uppercase" style={{ color: MUTED }}>ou</span>
        <div className="flex-1 h-px" style={{ backgroundColor: BORDER }} />
      </div>

      <div className="flex flex-wrap gap-3 mb-3">
        <div>
          <label className={labelCls} style={{ color: MUTED }}>Atleta específico</label>
          {athletesDim.error ? (
            <DimensionError label="os atletas" onRetry={athletesDim.reload} />
          ) : (
            <select className={inputCls} style={{ borderColor: BORDER, color: INK }}
              disabled={athletesDim.loading}
              value={athleteId}
              onChange={(e) => {
                const v = e.target.value;
                setAthleteId(v);
                if (v) { setCategoryId(''); setGender(''); setSquadId(''); }
                resetPreview();
              }}>
              <option value="">
                {athletesDim.loading ? 'Carregando…'
                  : athletesDim.items.length === 0 ? 'Nenhum atleta disponível'
                  : 'Nenhum'}
              </option>
              {athletesDim.items.map((a) => (
                <option key={a.athlete_user_id} value={a.athlete_user_id}>{a.athlete_name}</option>
              ))}
            </select>
          )}
        </div>
        {individual && (
          <p className="text-[11px] self-end pb-2" style={{ color: MUTED }}>
            Regra individual: categoria, sexo e grupo ficam desabilitados.
          </p>
        )}
      </div>

      {error && <p className="text-sm mb-3" style={{ color: '#b91c1c' }}>{error}</p>}

      {/* Preview obrigatório antes de salvar */}
      {preview && (
        <div className="border rounded-lg p-3 mb-3" style={{ borderColor: BORDER, backgroundColor: BG }}>
          <p className="text-xs font-black uppercase tracking-widest mb-2" style={{ color: MUTED }}>
            Preview — {preview.count} {preview.count === 1 ? 'atleta' : 'atletas'}
          </p>
          {preview.count === 0 ? (
            <p className="text-sm" style={{ color: MUTED }}>
              Nenhum atleta corresponde a esta responsabilidade atualmente.
            </p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {preview.athletes.map((a) => (
                <span key={a.athlete_user_id}
                  className="px-2 py-0.5 rounded border bg-white text-xs"
                  style={{ borderColor: BORDER, color: INK }}>
                  {a.athlete_name}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="flex gap-2">
        <button onClick={runPreview}
          disabled={previewing || !hasDimension() || dimensionBlocked}
          className="px-4 py-2 border rounded-lg text-sm font-bold disabled:opacity-50"
          style={{ borderColor: INK, color: INK }}>
          {previewing ? 'Calculando…' : 'Ver preview'}
        </button>
        <button onClick={save} disabled={saving || !preview || dimensionBlocked}
          className="px-4 py-2 rounded-lg text-sm font-bold text-white disabled:opacity-40"
          style={{ backgroundColor: INK }}
          title={!preview ? 'Gere o preview antes de salvar' : undefined}>
          {saving ? 'Salvando…' : 'Salvar regra'}
        </button>
        {dimensionBlocked ? (
          <span className="text-[11px] self-center" style={{ color: '#b91c1c' }}>
            Corrija o carregamento das dimensões antes de salvar.
          </span>
        ) : !preview && (
          <span className="text-[11px] self-center" style={{ color: MUTED }}>
            O preview é obrigatório antes de salvar.
          </span>
        )}
      </div>
    </div>
  );
};

/** INV-2d — Card de convite (visível só quando !has_account).
 *  Token é bearer credential: só existe em memória React após o POST
 *  (única rota que o devolve); o read model (pending_invite) nunca o expõe.
 *  E-mail/role vêm do CADASTRO — o POST não envia nenhum campo de identidade. */
const InviteCard = ({ staffId, staff, pendingInvite, onChanged }) => {
  const [sending, setSending] = useState(false);
  const [inviteErr, setInviteErr] = useState(null);
  // {url, reused} — resultado do último POST desta sessão de tela
  const [delivery, setDelivery] = useState(null);
  const [copied, setCopied] = useState(false);

  const generate = async () => {
    setSending(true); setInviteErr(null); setCopied(false);
    try {
      const { data } = await AZURE.post(`/gestor/staff/${staffId}/invite`, {});
      const token = data?.invite?.invite_token;
      if (!token) throw new Error('Resposta sem token');
      setDelivery({
        url: `${window.location.origin}/accept-invite?token=${encodeURIComponent(token)}`,
        reused: !!data.invite.reused,
        expiresAt: data.invite.expires_at,
        email: data.invite.invite_email,
      });
      // NÃO recarrega a ficha aqui: load() remontaria o card e perderíamos o
      // delivery (token só existe nesta resposta). O estado local já mostra
      // tudo; pending_invite atualiza na próxima visita à ficha.
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.error;
      if (status === 409 && /conta/i.test(msg || '')) {
        // Corrida: alguém vinculou a conta — recarrega e o card some sozinho.
        setInviteErr(msg);
        await onChanged();
      } else if (status === 422) {
        setInviteErr(msg || 'O profissional precisa ter e-mail cadastrado. Edite os dados na aba Dados.');
      } else {
        setInviteErr(msg || 'Erro ao gerar o convite. Tente novamente.');
      }
    } finally { setSending(false); }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(delivery.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      setInviteErr('Não foi possível copiar. Selecione o link manualmente.');
    }
  };

  const waHref = delivery ? `https://wa.me/?text=${encodeURIComponent(
    `Olá${staff.full_name ? `, ${staff.full_name}` : ''}! Você foi convidado(a) `
    + `para acessar a VUKZ AI. Crie sua conta ou entre pelo link: `
    + delivery.url)}` : null;

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('pt-BR') : null;

  return (
    <div className="bg-white border rounded-xl p-4 mb-4" style={{ borderColor: BORDER }}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-sm font-bold" style={{ color: INK }}>
            Convite de acesso
          </div>
          {!delivery && !pendingInvite && (
            <p className="text-sm mt-1" style={{ color: MUTED }}>
              Este profissional ainda não tem conta na plataforma. Gere um link
              de convite e envie por WhatsApp ou e-mail.
            </p>
          )}
          {!delivery && pendingInvite && (
            <p className="text-sm mt-1" style={{ color: MUTED }}>
              Convite pendente para <strong style={{ color: INK }}>{pendingInvite.invite_email}</strong>
              {pendingInvite.expires_at && <> · válido até {fmtDate(pendingInvite.expires_at)}</>}.
              {' '}O link não fica salvo — gere novamente para reenviar.
            </p>
          )}
        </div>
        <button onClick={generate} disabled={sending}
          className="px-3 py-2 rounded-lg text-xs font-bold text-white disabled:opacity-60"
          style={{ backgroundColor: INK }}>
          {sending ? 'Gerando…'
            : (pendingInvite || delivery) ? 'Gerar link novamente' : 'Gerar link de convite'}
        </button>
      </div>

      {inviteErr && (
        <div className="mt-3 text-sm rounded-lg border px-3 py-2"
          style={{ borderColor: '#fca5a5', color: '#b91c1c', backgroundColor: '#fef2f2' }}>
          {inviteErr}
        </div>
      )}

      {delivery && (
        <div className="mt-3 border rounded-lg p-3" style={{ borderColor: BORDER }}>
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="px-1.5 py-0.5 rounded border text-[10px] font-semibold"
              style={delivery.reused
                ? { borderColor: BORDER, color: MUTED }
                : { borderColor: '#a7f3d0', color: '#047857', backgroundColor: '#f0fdf4' }}>
              {delivery.reused ? 'link reaproveitado' : 'novo convite'}
            </span>
            {delivery.expiresAt && (
              <span className="text-[11px]" style={{ color: MUTED }}>
                válido até {fmtDate(delivery.expiresAt)}
              </span>
            )}
          </div>
          <code className="block text-xs break-all px-2 py-1.5 rounded border mb-2"
            style={{ borderColor: BORDER, color: INK, backgroundColor: BG }}>
            {delivery.url}
          </code>
          <div className="flex gap-2">
            <button onClick={copyLink}
              className="px-3 py-1.5 rounded-lg text-xs font-bold text-white"
              style={{ backgroundColor: copied ? '#047857' : INK }}>
              {copied ? 'Copiado!' : 'Copiar link'}
            </button>
            <a href={waHref} target="_blank" rel="noopener noreferrer"
              className="px-3 py-1.5 rounded-lg text-xs font-bold border"
              style={{ borderColor: BORDER, color: INK }}>
              Enviar por WhatsApp
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

const StaffDetailPage = () => {
  const { staffId } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // P2.3: a rota agora é a ficha canônica do profissional — abre em Dados.
  const [tab, setTab] = useState('overview');
  const [editorOpen, setEditorOpen] = useState(false);
  const [togglingRule, setTogglingRule] = useState(null);
  // P2.3 — aba Dados: edição + desativação. Erros LOCAIS (dataError),
  // nunca no `error` global, para não perder a ficha já carregada.
  const [editingData, setEditingData] = useState(false);
  const [savingData, setSavingData] = useState(false);
  const [dataError, setDataError] = useState(null);
  const [deactivating, setDeactivating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await AZURE.get(`/gestor/staff/${staffId}`);
      setDetail(data?.data || null);
    } catch (e) {
      setError(e?.response?.status === 404
        ? 'Profissional não encontrado neste clube'
        : (e?.response?.data?.error || 'Erro ao carregar o profissional'));
    } finally { setLoading(false); }
  }, [staffId]);

  useEffect(() => { load(); }, [load]);

  const toggleRule = async (rule) => {
    setTogglingRule(rule.rule_id);
    try {
      await AZURE.patch(`/gestor/staff/${staffId}/rules/${rule.rule_id}`,
        { is_active: !rule.is_active });
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || 'Erro ao atualizar a regra');
    } finally { setTogglingRule(null); }
  };

  // P2.3: edição canônica — PUT /roster/staff/:id (sp_save_club_staff).
  // load() reconstrói staff com phone/hire_date vindos do GET /gestor/staff/:id.
  const saveData = async (values) => {
    setSavingData(true); setDataError(null);
    try {
      await AZURE.put(`/roster/staff/${staffId}`, values);
      setEditingData(false);
      await load();
    } catch (e) {
      setDataError(e?.response?.data?.error || 'Erro ao salvar os dados');
    } finally { setSavingData(false); }
  };

  // P2.3: desativação canônica — DELETE /roster/staff/:id (soft delete).
  // Vocabulário de UI: sempre "Desativar", nunca "Excluir".
  const deactivate = async () => {
    if (!window.confirm(
      'Desativar este profissional? Ele deixa de aparecer nas listagens '
      + 'ativas, mas o histórico é preservado (não é uma exclusão).')) return;
    setDeactivating(true); setDataError(null);
    try {
      await AZURE.delete(`/roster/staff/${staffId}`);
      await load();
    } catch (e) {
      setDataError(e?.response?.data?.error || 'Erro ao desativar o profissional');
    } finally { setDeactivating(false); }
  };

  const staff = detail?.staff;
  const rules = detail?.rules || [];
  const activeRules = rules.filter((r) => r.is_active);
  const athletes = detail?.athletes || [];
  const permissions = detail?.permissions || [];

  return (
    <div className="min-h-screen" style={{ backgroundColor: BG }}>
      <div className="max-w-5xl mx-auto px-4 lg:px-6 py-6">
        {/* Voltar + header compacto */}
        <button onClick={() => navigate('/manager/staff')}
          className="text-xs font-bold mb-3 hover:underline" style={{ color: MUTED }}>
          ← Equipe
        </button>

        {error && (
          <div className="bg-white border rounded-xl p-4 mb-4 flex items-center justify-between"
            style={{ borderColor: '#fca5a5' }}>
            <span className="text-sm" style={{ color: '#b91c1c' }}>{error}</span>
            <button onClick={load}
              className="px-3 py-1.5 border rounded-lg text-xs font-bold"
              style={{ borderColor: BORDER, color: INK }}>
              Tentar novamente
            </button>
          </div>
        )}

        {loading ? (
          <div className="space-y-3">
            <div className="bg-white h-20 rounded-xl animate-pulse border" style={{ borderColor: BORDER }} />
            <div className="bg-white h-64 rounded-xl animate-pulse border" style={{ borderColor: BORDER }} />
          </div>
        ) : staff && (
          <>
            {/* Header do profissional */}
            <div className="bg-white rounded-xl border p-4 mb-4 flex flex-wrap items-center gap-4"
              style={{ borderColor: BORDER }}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-xl font-black tracking-tight truncate" style={{ color: INK }}>
                    {staff.full_name}
                  </h1>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold border"
                    style={staff.is_active
                      ? { borderColor: '#a7f3d0', color: '#047857', backgroundColor: '#f0fdf4' }
                      : { borderColor: BORDER, color: MUTED }}>
                    {staff.is_active ? 'Ativo' : 'Inativo'}
                  </span>
                  {!staff.has_account && (
                    <span className="px-1.5 py-0.5 rounded border text-[10px] font-semibold"
                      style={{ borderColor: BORDER, color: MUTED }}>
                      sem conta
                    </span>
                  )}
                </div>
                <p className="text-sm truncate" style={{ color: MUTED }}>
                  {ROLE_LABELS[staff.staff_role] || staff.staff_role}
                  {staff.email ? ` · ${staff.email}` : ''}
                </p>
              </div>
              <div className="text-right">
                <div className="text-2xl font-black" style={{ color: INK }}>
                  {detail.resolved_total}
                </div>
                <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: MUTED }}>
                  Atletas acessíveis
                </div>
              </div>
            </div>

            {!staff.has_account && (
              <InviteCard staffId={staffId} staff={staff}
                pendingInvite={detail.pending_invite} onChanged={load} />
            )}

            {/* Tabs */}
            <div className="flex gap-1 mb-4 border-b" style={{ borderColor: BORDER }}>
              {TABS.map((t) => (
                <button key={t.key} onClick={() => setTab(t.key)}
                  className="px-4 py-2 text-sm font-bold -mb-px border-b-2 transition-colors"
                  style={tab === t.key
                    ? { color: INK, borderColor: INK }
                    : { color: MUTED, borderColor: 'transparent' }}>
                  {t.label}
                  {t.key === 'rules' && activeRules.length > 0 && ` (${activeRules.length})`}
                  {t.key === 'athletes' && ` (${athletes.length})`}
                </button>
              ))}
            </div>

            {/* ── Dados (P2.3: visualização + edição + desativação) ── */}
            {tab === 'overview' && (
              <div className="bg-white rounded-xl border p-5" style={{ borderColor: BORDER }}>
                {editingData ? (
                  <>
                    <h2 className="text-sm font-black mb-4" style={{ color: INK }}>Editar dados</h2>
                    <ProfessionalForm
                      mode="edit"
                      initialValues={staff}
                      onSubmit={saveData}
                      onCancel={() => { setEditingData(false); setDataError(null); }}
                      loading={savingData}
                      error={dataError}
                    />
                  </>
                ) : (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-sm font-black" style={{ color: INK }}>Dados do profissional</h2>
                      <button onClick={() => { setEditingData(true); setDataError(null); }}
                        className="px-3 py-1.5 border rounded-lg text-xs font-bold"
                        style={{ borderColor: INK, color: INK }}>
                        Editar
                      </button>
                    </div>
                    <dl className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {[
                        ['Nome', staff.full_name],
                        ['Função', ROLE_LABELS[staff.staff_role] || staff.staff_role],
                        ['E-mail', staff.email || '—'],
                        ['Telefone', staff.phone || '—'],
                        ['Contratação', staff.hire_date ? String(staff.hire_date).slice(0, 10) : '—'],
                        ['Regras ativas', String(activeRules.length)],
                        ['Atletas acessíveis', String(detail.resolved_total)],
                      ].map(([k, v]) => (
                        <div key={k}>
                          <dt className="text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: MUTED }}>{k}</dt>
                          <dd className="text-sm font-bold" style={{ color: INK }}>{v}</dd>
                        </div>
                      ))}
                    </dl>
                    <p className="text-xs mt-5" style={{ color: MUTED }}>
                      O acesso efetivo aos atletas é resolvido dinamicamente pela
                      plataforma (regras de responsabilidade + vínculos existentes).
                      A lista nunca é congelada: mudanças no elenco refletem
                      automaticamente.
                    </p>
                    {dataError && (
                      <p className="text-sm mt-3" style={{ color: '#b91c1c' }}>{dataError}</p>
                    )}
                    {staff.is_active && (
                      <div className="mt-5 pt-4 border-t" style={{ borderColor: BORDER }}>
                        <button onClick={deactivate} disabled={deactivating}
                          className="px-3 py-1.5 border rounded-lg text-xs font-bold disabled:opacity-50"
                          style={{ borderColor: '#fca5a5', color: '#b91c1c' }}>
                          {deactivating ? 'Desativando…' : 'Desativar profissional'}
                        </button>
                        <p className="text-[11px] mt-2" style={{ color: MUTED }}>
                          A desativação preserva o histórico; não é uma exclusão.
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* ── Responsabilidades ── */}
            {tab === 'rules' && (
              <div className="bg-white rounded-xl border p-5" style={{ borderColor: BORDER }}>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-sm font-black" style={{ color: INK }}>Regras de responsabilidade</h2>
                    <p className="text-xs" style={{ color: MUTED }}>
                      Cada regra soma atletas (OU). Para alterar uma regra,
                      desative-a e crie uma nova.
                    </p>
                  </div>
                  {!editorOpen && (
                    <button onClick={() => setEditorOpen(true)}
                      className="px-3 py-2 rounded-lg text-xs font-bold text-white"
                      style={{ backgroundColor: INK }}>
                      + Nova regra
                    </button>
                  )}
                </div>

                {editorOpen && (
                  <RuleEditor staffId={staffId}
                    onCancel={() => setEditorOpen(false)}
                    onCreated={() => { setEditorOpen(false); load(); }} />
                )}

                {rules.length === 0 ? (
                  <p className="text-sm py-6 text-center" style={{ color: MUTED }}>
                    Nenhuma regra definida.
                    {athletes.length > 0 && ' O acesso atual vem de vínculos diretos.'}
                  </p>
                ) : (
                  <div className="divide-y" style={{ borderColor: BORDER }}>
                    {rules.map((r) => (
                      <div key={r.rule_id} className="py-3 flex items-center gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-bold truncate"
                            style={{ color: r.is_active ? INK : MUTED }}>
                            {r.label}
                            {!r.is_active && (
                              <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded border"
                                style={{ borderColor: BORDER, color: MUTED }}>
                                desativada
                              </span>
                            )}
                          </div>
                          <div className="text-xs" style={{ color: MUTED }}>
                            {r.athlete_user_id ? 'Individual' : 'Dinâmica'}
                            {' · '}{r.resolved_count} {r.resolved_count === 1 ? 'atleta' : 'atletas'} hoje
                          </div>
                        </div>
                        <button onClick={() => toggleRule(r)}
                          disabled={togglingRule === r.rule_id}
                          className="px-3 py-1.5 border rounded-lg text-xs font-bold disabled:opacity-50"
                          style={{ borderColor: BORDER, color: r.is_active ? '#b91c1c' : INK }}>
                          {togglingRule === r.rule_id ? '…'
                            : r.is_active ? 'Desativar' : 'Reativar'}
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <p className="text-xs mt-4 pt-3 border-t" style={{ color: MUTED, borderColor: BORDER }}>
                  Total resolvido (união sem duplicatas): <strong style={{ color: INK }}>
                    {detail.resolved_total}</strong> atletas acessíveis.
                </p>
              </div>
            )}

            {/* ── Permissões (read-only) ── */}
            {tab === 'permissions' && (
              <div className="bg-white rounded-xl border p-5" style={{ borderColor: BORDER }}>
                <h2 className="text-sm font-black mb-1" style={{ color: INK }}>Permissões por ferramenta</h2>
                <p className="text-xs mb-4" style={{ color: MUTED }}>
                  Reflexo do controle de acesso vigente (somente leitura aqui).
                </p>
                {permissions.length === 0 ? (
                  <p className="text-sm py-6 text-center" style={{ color: MUTED }}>
                    {staff.has_account
                      ? 'Nenhuma permissão específica configurada.'
                      : 'Profissional sem conta — sem permissões aplicáveis.'}
                  </p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {permissions.map((p) => (
                      <div key={p.tool_key}
                        className="flex items-center justify-between border rounded-lg px-3 py-2"
                        style={{ borderColor: BORDER }}>
                        <span className="text-sm font-semibold" style={{ color: INK }}>
                          {TOOL_LABELS[p.tool_key] || p.tool_key}
                        </span>
                        <span className="text-xs font-bold" style={{ color: MUTED }}>
                          {p.can_write ? 'Leitura e escrita' : p.can_read ? 'Somente leitura' : '—'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── Atletas ── */}
            {tab === 'athletes' && (
              <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: BORDER }}>
                <div className="px-5 py-3 border-b" style={{ borderColor: BORDER }}>
                  <h2 className="text-sm font-black" style={{ color: INK }}>
                    Atletas acessíveis ({athletes.length})
                  </h2>
                  <p className="text-xs" style={{ color: MUTED }}>
                    Resolução dinâmica pela plataforma — “Acesso por” explica a origem.
                  </p>
                </div>
                {athletes.length === 0 ? (
                  <p className="text-sm py-8 text-center" style={{ color: MUTED }}>
                    {staff.has_account
                      ? 'Nenhum atleta acessível no momento.'
                      : 'Profissional sem conta — nenhum acesso efetivo ainda.'}
                  </p>
                ) : athletes.map((a) => (
                  <div key={a.athlete_user_id}
                    className="px-5 py-2.5 border-b last:border-b-0 flex flex-wrap items-center gap-2"
                    style={{ borderColor: BORDER }}>
                    <div className="min-w-0 flex-1">
                      <span className="text-sm font-bold" style={{ color: INK }}>{a.athlete_name}</span>
                      {a.category && (
                        <span className="text-xs ml-2" style={{ color: MUTED }}>{a.category}</span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {(a.access_sources || []).map((s, i) => (
                        <span key={i}
                          className="px-2 py-0.5 rounded border text-[10px] font-semibold"
                          style={{ borderColor: BORDER, color: MUTED, backgroundColor: BG }}>
                          Acesso por: {(SOURCE_LABELS[s.type] || (() => s.type))(s)}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default StaffDetailPage;
